import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, ChevronDown, ChevronUp, Circle, Loader2 } from 'lucide-react';
import { Badge } from '../../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700 border border-gray-300',
  sent: 'bg-blue-100 text-blue-800 border border-blue-300',
  signed: 'bg-green-100 text-green-800 border border-green-300',
  archived: 'bg-gray-200 text-gray-600 border border-gray-300',
  voided: 'bg-red-100 text-red-700 border border-red-300',
};

const TYPE_COLORS = {
  loi: 'bg-[#023E8A] text-white',
  nda: 'bg-[#FF6B35] text-white',
  ncnda: 'bg-[#DC2626] text-white',
  confidentiality: 'bg-[#00B4D8] text-white',
  cover_letter: 'bg-[#6C757D] text-white',
  partnership: 'bg-[#198754] text-white',
  spa: 'bg-[#8B5CF6] text-white',
};

/**
 * Inline section listing every document already generated for a given counterparty.
 * Collapsed by default, expands on click.
 */
const SupplierDocuments = ({ supplierId, supplierName }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [count, setCount] = useState(null);

  // Fetch count eagerly for badge even when collapsed
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/crm/generated-documents/?counterparty_id=${supplierId}&limit=100`);
        if (active) { setDocs(r.data || []); setCount((r.data || []).length); }
      } catch (_) {
        if (active) { setDocs([]); setCount(0); }
      }
    })();
    return () => { active = false; };
  }, [supplierId]);

  const refetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/crm/generated-documents/?counterparty_id=${supplierId}&limit=100`);
      setDocs(r.data || []);
      setCount((r.data || []).length);
    } finally { setLoading(false); }
  };

  const handleStatusChange = async (doc, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/crm/generated-documents/${doc.id}`, { status: newStatus });
      await refetch();
    } catch (_) { /* toast handled upstream if needed */ }
  };

  if (count === 0 && !open) return null; // keep the card clean when no docs exist

  return (
    <div className="mt-4 border-t pt-3" data-testid={`supplier-documents-${supplierId}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-medium text-[#023E8A] hover:underline"
        data-testid={`supplier-documents-toggle-${supplierId}`}
      >
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        <FileText className="w-4 h-4" />
        CRM Documents
        <Badge className="bg-[#023E8A] text-white">{count ?? '…'}</Badge>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {loading && (
            <div className="text-xs text-[#6C757D] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          )}
          {!loading && docs.length === 0 && (
            <div className="text-xs text-[#6C757D] py-2">No documents generated yet. Open the Document Templates CMS to create the first one.</div>
          )}
          {!loading && docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between bg-gray-50 border rounded p-2" data-testid={`supplier-doc-row-${d.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={TYPE_COLORS[d.template_type] || 'bg-gray-500 text-white'}>{(d.template_type || '').toUpperCase()}</Badge>
                  <span className="text-sm font-semibold text-[#2B2D42]">{d.template_name || d.template_code}</span>
                  <code className="text-[10px] text-[#6C757D] bg-white px-1.5 py-0.5 rounded">{d.language?.toUpperCase()}</code>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLES[d.status] || STATUS_STYLES.draft}`}>
                    <Circle className="w-2 h-2 inline mr-1 fill-current" />{d.status}
                  </span>
                </div>
                <div className="text-[11px] text-[#6C757D] mt-0.5">
                  {formatDate(d.generated_at)} · file: {d.filename || `${d.template_code}.pdf`}
                  {d.notes && <span className="ml-2 italic">· {d.notes}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <select
                  value={d.status}
                  onChange={(e) => handleStatusChange(d, e.target.value)}
                  className="text-[11px] px-1.5 py-1 border rounded bg-white"
                  data-testid={`supplier-doc-status-${d.id}`}
                >
                  <option value="draft">draft</option>
                  <option value="sent">sent</option>
                  <option value="signed">signed</option>
                  <option value="archived">archived</option>
                  <option value="voided">voided</option>
                </select>
                <a
                  href={`${API_URL}/api/crm/generated-documents/${d.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="p-1.5 rounded border bg-white hover:bg-gray-100"
                  title="Download / preview PDF"
                  data-testid={`supplier-doc-download-${d.id}`}
                >
                  <Download className="w-3.5 h-3.5 text-[#023E8A]" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) { return iso; }
}

export default SupplierDocuments;
